import { type Vec2, vec } from '../math';

import { evaluateAt } from './evaluate';
import { type Node } from './parse';

/**
 * Turning a function into points a renderer can draw.
 *
 * ## Why this is the hard part
 *
 * Evaluating `1/x` at a thousand points is trivial. Drawing it without a vertical line through
 * the asymptote is not, and that line is the single most recognisable mark of a grapher that
 * was not finished. The same defect draws `tan(x)` as a picket fence joined at the top, and
 * `floor(x)` as a staircase with diagonal risers.
 *
 * So the output is **segments**, not points: a list of runs, each of which may be joined up,
 * with a break wherever the function stops being continuous. Everything below exists to decide
 * where those breaks go.
 *
 * ## Three kinds of break, and how each is found
 *
 * 1. **Undefined.** `evaluateAt` returns `null` — outside a domain, a division by zero exactly
 *    on a sample. Break, and refine towards the edge so the visible end of the curve lands on
 *    the boundary rather than up to one step short of it.
 *
 * 2. **An odd pole.** The sign flips *and* the magnitude either side is far above what the
 *    function does elsewhere. Both halves matter: a sign change alone is every zero crossing,
 *    and a large magnitude alone is `exp`.
 *
 * 3. **A jump.** `floor`, `sign` -- the value changes by a lot between neighbouring samples
 *    with no pole in sight. Found by comparing a step against **its own neighbours**, not
 *    against a global average.
 *
 * ## The measurement that shaped this
 *
 * The first version compared every step to the *median step across the whole range*, and it
 * was wrong in both directions at once. `1/x` came back as **32** pieces instead of two,
 * because the function is nearly flat far from the pole, so the median is tiny and every step
 * within a mile of zero exceeds it. `x^5` came back as **102** pieces for the same reason
 * inverted -- it is steep at both ends and flat in the middle.
 *
 * The lesson is that "a big step" is meaningless globally. A step is only big *compared to the
 * steps beside it*, and a magnitude only big *compared to the magnitudes elsewhere*. Those are
 * two different comparisons and both are needed.
 *
 * ## Why not adaptive subdivision everywhere
 *
 * Recursive refinement by curvature gives beautiful curves and unbounded cost, and it is
 * unbounded exactly where the function is nastiest — which is where a teacher is most likely
 * to be typing. A fixed budget with refinement spent only near breaks gives a curve that is
 * smooth enough at any reasonable zoom and a cost that cannot run away.
 */

export interface SampleOptions {
  /** Inclusive range of `x`. */
  readonly from: number;
  readonly to: number;
  /** How many points to spend. The default is smooth at ordinary zoom and cheap to redraw. */
  readonly samples?: number;
}

/** How many times its neighbours a step must be before it counts as a jump. */
const JUMP_FACTOR = 25;

/** How many times the typical magnitude a value must reach before it counts as near a pole. */
const POLE_FACTOR = 30;

/** Refinement steps spent narrowing each break. Nine halvings is a 500× narrower gap. */
const REFINEMENTS = 9;

export const DEFAULT_SAMPLES = 600;

/** The most points a single graph may produce, whatever it is asked for. */
const MAX_SAMPLES = 4000;

/**
 * Samples `node` across the range, returning one run of points per continuous piece.
 *
 * A run with fewer than two points is dropped: a single point is not a curve, and a lone
 * sample surviving between two breaks is far more likely to be an artefact of the sampling
 * than a feature of the function.
 */
export function sampleFunction(node: Node, options: SampleOptions): readonly (readonly Vec2[])[] {
  const { from, to } = options;
  if (!Number.isFinite(from) || !Number.isFinite(to) || from >= to) return [];

  const count = Math.min(Math.max(Math.floor(options.samples ?? DEFAULT_SAMPLES), 2), MAX_SAMPLES);
  const step = (to - from) / (count - 1);

  const xs: number[] = [];
  const ys: (number | null)[] = [];
  for (let index = 0; index < count; index += 1) {
    const x = index === count - 1 ? to : from + index * step;
    xs.push(x);
    ys.push(evaluateAt(node, x));
  }

  const scale = typicalMagnitude(ys);
  const deltas = stepSizes(ys);

  const segments: Vec2[][] = [];
  let run: Vec2[] = [];

  const close = (): void => {
    if (run.length >= 2) segments.push(run);
    run = [];
  };

  for (let index = 0; index < count; index += 1) {
    const y = ys[index] ?? null;
    const x = xs[index] ?? 0;
    const previousX = index === 0 ? x : (xs[index - 1] ?? x);

    if (y === null) {
      if (run.length > 0) {
        // Walk towards the hole so the drawn end sits on the boundary of the domain rather
        // than up to a whole step short of it — the visible difference at the edge of
        // `sqrt(x)` or `ln(x)` between a curve that reaches zero and one that stops near it.
        const edge = refine(node, previousX, x);
        if (edge !== null) run.push(edge);
      }
      close();
      continue;
    }

    /*
     * The top of an even-order spike: `1/x^2` is enormous and *positive* on both sides, so
     * neither a sign change nor a large step gives it away. What does is that it is a local
     * maximum of magnitude, far above anything the function does elsewhere. Without this the
     * two arms are joined by a horizontal lid across the top of the spike.
     */
    if (isSpikeTop(ys, index, scale)) {
      close();
      continue;
    }

    const previous = index === 0 ? null : (ys[index - 1] ?? null);
    if (run.length > 0 && previous !== null && isBreak(previous, y, deltas, index, scale)) {
      /*
       * A pole with both sides defined: the break has to be *inserted* rather than discovered
       * as a gap. The curve is carried towards the asymptote from each side, which is what
       * makes `1/x` look like `1/x` instead of two curves stopping early.
       */
      const before = refine(node, previousX, x);
      if (before !== null) run.push(before);
      close();

      const after = refine(node, x, previousX);
      if (after !== null) run.push(after);
    }

    run.push(vec.vec2(x, y));
  }

  close();
  return segments;
}

/** |Δy| between each adjacent pair, or `null` where either end is undefined. */
function stepSizes(ys: readonly (number | null)[]): readonly (number | null)[] {
  const deltas: (number | null)[] = [];
  for (let index = 1; index < ys.length; index += 1) {
    const a = ys[index - 1] ?? null;
    const b = ys[index] ?? null;
    deltas.push(a === null || b === null ? null : Math.abs(b - a));
  }
  return deltas;
}

/**
 * The typical size of |y|, used as the scale a magnitude is judged against.
 *
 * The **median**, because a pole contributes a handful of astronomical values that would drag
 * a mean past every threshold and make nothing look extreme.
 */
function typicalMagnitude(ys: readonly (number | null)[]): number {
  const magnitudes = ys.filter((y): y is number => y !== null).map(Math.abs);
  if (magnitudes.length === 0) return 0;

  magnitudes.sort((a, b) => a - b);
  return magnitudes[Math.floor(magnitudes.length / 2)] ?? 0;
}

/** Whether the pair ending at `index` straddles a discontinuity. */
function isBreak(
  previous: number,
  current: number,
  deltas: readonly (number | null)[],
  index: number,
  scale: number
): boolean {
  // An odd pole: the sign flips *and* both sides are far beyond what the function does
  // elsewhere. Either half alone is ordinary — a sign change is every zero crossing, and a
  // large magnitude is `exp`.
  const flipped = previous < 0 !== current < 0;
  const extreme =
    Math.min(Math.abs(previous), Math.abs(current)) > Math.max(scale * POLE_FACTOR, 1);
  if (flipped && extreme) return true;

  /*
   * A jump, measured against its own neighbours rather than against the range.
   *
   * `floor` steps by one where the neighbouring steps are zero; a steep but continuous curve
   * has steps that change gradually, so each is comparable to the one beside it however large
   * they all are. That comparison is what tells the two apart, and a global average cannot.
   */
  const here = deltas[index - 1] ?? null;
  if (here === null) return false;

  const before = deltas[index - 2] ?? null;
  const after = deltas[index] ?? null;
  const neighbours = [before, after].filter((delta): delta is number => delta !== null);
  if (neighbours.length === 0) return false;

  const local = Math.max(...neighbours);
  return here > Math.max(local * JUMP_FACTOR, 1e-9);
}

/** Whether `index` is the tip of an even-order pole — a local maximum of extreme magnitude. */
function isSpikeTop(ys: readonly (number | null)[], index: number, scale: number): boolean {
  const y = ys[index] ?? null;
  const before = ys[index - 1] ?? null;
  const after = ys[index + 1] ?? null;
  if (y === null || before === null || after === null) return false;

  const magnitude = Math.abs(y);
  if (magnitude <= Math.max(scale * POLE_FACTOR, 1)) return false;

  return magnitude >= Math.abs(before) && magnitude >= Math.abs(after);
}

/**
 * Walks from a known-good `x` towards a bad one, returning the last point that still works.
 *
 * Bisection rather than a smaller uniform step: nine halvings narrow the gap five hundred
 * fold for nine evaluations, where a uniform sweep of the same accuracy would cost five
 * hundred. The curve therefore reaches visibly *to* the asymptote at any zoom a user will
 * apply, for a cost that does not depend on how close they look.
 */
function refine(node: Node, good: number, bad: number): Vec2 | null {
  let low = good;
  let high = bad;
  let best: Vec2 | null = null;

  const goodValue = evaluateAt(node, good);
  if (goodValue === null) return null;

  const reference = Math.abs(goodValue);

  for (let step = 0; step < REFINEMENTS; step += 1) {
    const middle = (low + high) / 2;
    const value = evaluateAt(node, middle);

    /*
     * "Still good" means defined *and* not already run away. Testing only for definedness
     * would let the refinement march right up to a pole and emit a point at 1e300, which the
     * renderer then has to cope with — and which makes the curve's bounding box useless.
     */
    if (value !== null && Math.abs(value) < Math.max(reference * 1e3, 1e6)) {
      best = vec.vec2(middle, value);
      low = middle;
    } else {
      high = middle;
    }
  }

  return best;
}
